import { Card, Nav, ProgressBar } from 'react-bootstrap';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from '@/components/common/Flex';
import { useAppContext } from '@/Main';
import {
  Navigate,
  Outlet,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { getColor } from '@/helpers/utils';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import accessLevelApi from '@/app/common/api/accessLevelApi';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { setDomain } from '@/app/common/reducers/domainSlice';
import { useMembershipWizard } from '../context/MembershipWizardContext';
import type {
  NavItemComponentProps,
  NavItemConfig,
  SelectedDomain,
  WizardProps,
} from '../types';

const ALL_NAV_ITEMS: NavItemConfig[] = [
  {
    id: 'plan',
    icon: 'window-restore',
    label: 'انتخاب طرح',
    path: '',
  },
  {
    id: 'payment',
    icon: 'credit-card',
    label: 'پرداخت',
    path: '/membership-steps/payment',
  },
  {
    id: 'domains',
    icon: 'globe',
    label: 'دامنه ها',
    path: '/membership-steps/domains',
  },
  {
    id: 'keywords',
    icon: 'chart-line',
    label: 'کلمات',
    path: '/membership-steps/keywords',
  },
];

type RootStateWithDomain = {
  selectedDomain: SelectedDomain;
};

const WizardLayout = ({ variant, validation: _validation, progressBar }: WizardProps) => {
  const { data: userInfo, isLoading } = useGetUserInfoQuery();
  const { isRTL: _isRTL } = useAppContext();
  const pathname = useLocation().pathname;
  const [_searchParams, _setSearchParams] = useSearchParams();
  const selectedDomain = useSelector(
    (state: RootStateWithDomain) => state.selectedDomain,
  );
  const dispatch = useDispatch();
  const membershipWizard = useMembershipWizard();
  const skipPaymentStep = membershipWizard?.skipPaymentStep ?? false;
  const skipKeywordsStep = membershipWizard?.skipKeywordsStep ?? false;

  const navItems = ALL_NAV_ITEMS.filter(
    item =>
      !(skipPaymentStep && item.id === 'payment') &&
      !(skipKeywordsStep && item.id === 'keywords'),
  ).map((item, index) => ({
    ...item,
    step: index + 1,
  }));

  const pathToNavItem = () => {
    return [...navItems]
      .reverse()
      .find(item => pathname?.includes(item.path));
  };

  const [currentNavItem, setCurrentNavItem] = useState<
    NavItemConfig | undefined
  >(navItems[0]);

  useEffect(() => {
    setCurrentNavItem(pathToNavItem());
  }, [pathname, skipPaymentStep, skipKeywordsStep]);

  if (isLoading) return '';
  if (
    skipKeywordsStep &&
    pathname.includes('membership-steps/keywords')
  ) {
    return <Navigate to="/" replace />;
  }
  if (userInfo?.data?.domains?.length > 0) {
    if (
      pathname.includes('membership-steps/domains') ||
      pathname.includes('membership-steps/keywords')
    ) {
      if (userInfo?.data?.domains.length !== 0) {
        const domains = userInfo?.data?.domains ?? [];
        // دامنه persist‌شده فقط وقتی معتبر است که متعلق به کاربر فعلی باشد
        const isSelectedDomainOwnedByUser = domains.some(
          (d: { slug?: string }) => d.slug === selectedDomain?.slug,
        );

        if (
          _.isEmpty(selectedDomain) ||
          selectedDomain?.name === 'no-domain' ||
          !isSelectedDomainOwnedByUser
        ) {
          dispatch(
            setDomain({
              name: domains[0]?.url,
              slug: domains[0]?.slug,
            }),
          );
        }

        dispatch(accessLevelApi.util.invalidateTags(['AccessLevel']));
      }
    } else return <Navigate to="/" />;
  }

  return (
    <>
      <Card className="theme-wizard mb-5">
        <Card.Header
          className={classNames('bg-body-tertiary', {
            'px-4 py-3': variant === 'pills',
            'pb-2': !variant,
          })}
        >
          <Nav
            className="justify-content-center"
            variant={variant === 'pills' ? 'pills' : undefined}
          >
            {variant === 'pills'
              ? navItems.map((item, index) => (
                  <NavItemPill
                    key={item.label}
                    index={index + 1}
                    step={currentNavItem?.step}
                    icon={item.icon}
                    path={item.path}
                    label={item.label}
                  />
                ))
              : navItems.map((item, index) => (
                  <NavItem
                    key={item.label}
                    index={index + 1}
                    path={item.path}
                    step={currentNavItem?.step}
                    icon={item.icon}
                    label={item.label}
                  />
                ))}
          </Nav>
        </Card.Header>
        {progressBar && (
          <ProgressBar
            now={((currentNavItem?.step ?? 0) / navItems.length) * 100}
            style={{ height: 2 }}
          />
        )}
        <Card.Body className="fw-normal p-0">
          <Outlet />
        </Card.Body>
      </Card>
    </>
  );
};

const NavItem = ({
  index,
  step,
  icon,
  label,
  path,
}: NavItemComponentProps) => {
  const isClickable = false;
  return isClickable ? (
    <Nav.Item>
      <Nav.Link
        to={step && step > index ? path : '#'}
        as={Link}
        className={classNames('fw-semi-bold', {
          done: step && step > index,
          active: step === index,
        })}
      >
        <span className="nav-item-circle-parent">
          <span className="nav-item-circle">
            <FontAwesomeIcon icon={icon} />
          </span>
        </span>
        <span
          style={{
            color: getColor('navbar-vertical-default-link-active-color'),
          }}
          className="d-none d-md-block mt-1 fs--1"
        >
          {label}
        </span>
      </Nav.Link>
    </Nav.Item>
  ) : (
    <Nav.Item>
      <Nav.Link
        className={classNames('fw-semi-bold', {
          done: step && step > index,
          active: step === index,
        })}
      >
        <span className="nav-item-circle-parent">
          <span className="nav-item-circle">
            <FontAwesomeIcon icon={icon} />
          </span>
        </span>
        <span
          style={{
            color:
              step === index
                ? getColor('navbar-vertical-default-link-active-color')
                : undefined,
          }}
          className="d-none d-md-block mt-1 fs--1"
        >
          {label}
        </span>
      </Nav.Link>
    </Nav.Item>
  );
};

const NavItemPill = ({
  index,
  step,
  icon,
  label,
}: NavItemComponentProps) => {
  return (
    <Nav.Item>
      <Nav.Link
        as={Link}
        className={classNames('fw-semi-bold', {
          done: step && step > index,
        })}
        style={{
          backgroundColor:
            step === index ? getColor('primary') : undefined,
          color: step === index ? getColor('gray-100') : undefined,
        }}
      >
        <Flex alignItems="center" justifyContent="center">
          <FontAwesomeIcon icon={icon} />
          <span className="d-none d-md-block mt-1 fs--1 ms-2">{label}</span>
        </Flex>
      </Nav.Link>
    </Nav.Item>
  );
};

export default WizardLayout;
